import React, { useState, useEffect, useRef, useMemo } from 'react';
import { reportHandledError } from '../utils/sentry';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Search, MapPin, ChevronUp, ChevronDown,
  RefreshCcw, ArrowLeft, TrendingUp, ShoppingBag, Info, Printer,
  History as HistoryLog, FileText, Building2, Calendar, AlertTriangle, Trash2, X, CreditCard
} from 'lucide-react';
import toast from 'react-hot-toast';
import QRCode from 'qrcode';
import { getSaleItemDisplayName } from '../utils/productLabels';
import { buildAfipQrUrl, buildFiscalTicketHtml, resolveTicketEmitter } from '../utils/ticketFiscal';
import CobrosAVerificar from '../components/CobrosAVerificar';
import {
  attachStoreOrdersToSales,
  isSaleAwaitingWebFulfillment,
  SALE_STORE_ORDER_SELECT,
} from '../utils/saleStoreOrders';
import { buildSalePaymentTerms } from '../utils/salesReceivables';
import { annulSaleWithReversal } from '../utils/saleAnnulment';
import { retractCrmSaleMovement } from '../utils/crmCommercial';
import { useSite } from '../context/SiteContext';

/**
 * VentasHistorial - Estandarizado al modelo "Productos" (Impulza ERP)
 * Compatible con la estrategia de "Hora Forjada" del POS.
 */
const LEGACY_CRM_SOURCES = ['crm_hito', 'crm_proyecto', 'crm_ticket', 'crm_schedule'];
const LEGACY_CRM_SOURCE_FILTER = LEGACY_CRM_SOURCES.join(',');

const isCrmSale = (sale) =>
  String(sale?.billing_source || '').trim().toLowerCase().startsWith('crm_')
  || String(sale?.payment_method || '').trim().toLowerCase() === 'cuenta corriente crm';

const buildHistorySiteFilter = (siteId) =>
  `site_id.eq.${siteId},and(site_id.is.null,billing_source.in.(${LEGACY_CRM_SOURCE_FILTER}))`;

const normalizePaymentMethod = (value) => String(value || '').trim().toLowerCase();

const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_RANGE_DAYS = 7;
const PAGE_SIZE_OPTIONS = [25, 50, 100];

// ============================================================================
// Combos en sale_items: las ventas de promo generan 1 fila parent + N filas
// component. Esta función aplana esa estructura en una lista visual ordenada
// donde cada componente sale debajo de su parent.
// ============================================================================
const buildSaleItemsDisplayList = (saleItems) => {
  if (!Array.isArray(saleItems) || saleItems.length === 0) return [];
  // Pre-clasificar
  const parents = [];
  const componentsByParentId = new Map();
  const standalones = [];
  for (const it of saleItems) {
    if (it.combo_role === 'component') {
      const key = it.parent_combo_product_id || '__orphan__';
      if (!componentsByParentId.has(key)) componentsByParentId.set(key, []);
      componentsByParentId.get(key).push(it);
    } else if (it.combo_role === 'parent') {
      parents.push(it);
    } else {
      standalones.push(it);
    }
  }
  // Aplanar: cada parent y sus componentes a continuación, luego standalones.
  // (Mantenemos parents y standalones intercalados según orden original.)
  const orderedTop = saleItems.filter((it) => it.combo_role !== 'component');
  const result = [];
  for (const top of orderedTop) {
    result.push({ ...top, isComboComponent: false });
    if (top.combo_role === 'parent' && top.product_id) {
      const comps = componentsByParentId.get(top.product_id) || [];
      for (const c of comps) result.push({ ...c, isComboComponent: true });
      componentsByParentId.delete(top.product_id);
    }
  }
  // Orphan components (no encontraron parent): mostrarlos al final como sueltos.
  for (const comps of componentsByParentId.values()) {
    for (const c of comps) result.push({ ...c, isComboComponent: false });
  }
  return result;
};

// Cuenta "items lógicos" desde el punto de vista del cliente: parents y sueltos
// suman 1, los componentes ya están representados por el parent.
const countLogicalSaleItems = (saleItems) =>
  Array.isArray(saleItems) ? saleItems.filter((it) => it.combo_role !== 'component').length : 0;

const resolveSaleMovementType = (sale) => {
  const source = String(sale?.billing_source || '').trim().toLowerCase();
  if (source === 'crm_hito') return 'Liquidacion hito CRM';
  if (source === 'crm_proyecto') return 'Liquidacion proyecto CRM';
  if (source === 'crm_ticket') return 'Liquidacion ticket CRM';
  if (source === 'crm_schedule') return 'Venta cronograma CRM';
  if (isCrmSale(sale)) return 'Venta CRM';
  return 'Venta POS';
};

function VentasHistorial() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  // ✅ FIX bug #2 multi-sucursal: si el usuario cambia de sucursal desde el Topbar,
  // un useEffect re-sincroniza el filtro local (más abajo). Al abrir la pantalla,
  // en cambio, el historial arranca sin filtrar por sucursal.
  const { selectedSiteId: contextSiteId } = useSite();

  const [loading, setLoading] = useState(true);
  const [ventas, setVentas] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [metodosCobro, setMetodosCobro] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  // Estados para anulación
  const [procesandoAnulacion, setProcesandoAnulacion] = useState(false);
  const [confirmingAnulacionId, setConfirmingAnulacionId] = useState(null);

  // Maestros para multi-CUIT
  const [orgData, setOrgData] = useState(null);
  const [billingEntities, setBillingEntities] = useState([]);

  // Configuración de Impresión
  const [printConfig, setPrintConfig] = useState(null);
  const [printModal, setPrintModal] = useState({ open: false, html: '' });
  const iframeRef = useRef(null);

  // --- FECHAS: Usamos ISO string simple para los inputs type="date"
  const getHoyInput = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Ventana por defecto. Arrancaba en "hoy": una venta de ayer quedaba fuera del
  // rango inicial y parecía no existir, sin ninguna señal de que había un filtro.
  const getInicioPorDefecto = () => {
    const desde = new Date();
    desde.setDate(desde.getDate() - (DEFAULT_RANGE_DAYS - 1));
    const year = desde.getFullYear();
    const month = String(desde.getMonth() + 1).padStart(2, '0');
    const day = String(desde.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [filtroFechaInicio, setFiltroFechaInicio] = useState(getInicioPorDefecto());
  const [filtroFechaFin, setFiltroFechaFin] = useState(getHoyInput());

  // Arranca sin filtrar por sucursal: filtrar por la sucursal activa escondía las
  // ventas de las otras sin que el usuario supiera que había un filtro puesto.
  const [filtroSitio, setFiltroSitio] = useState('');
  const [filtroEmisor, setFiltroEmisor] = useState('');
  const [filtroMetodoCobro, setFiltroMetodoCobro] = useState('');

  // ✅ FIX bug #2: si el usuario cambia de sucursal en el Topbar mientras está en
  // el historial, sincronizamos el filtro local con el SiteContext.
  // El primer render se saltea a propósito: aplicar la sucursal activa al abrir
  // la pantalla es lo que ocultaba las ventas de las demás sucursales.
  const contextSiteSyncReady = useRef(false);
  useEffect(() => {
    if (!contextSiteSyncReady.current) {
      contextSiteSyncReady.current = true;
      return;
    }
    setFiltroSitio(contextSiteId || '');
  }, [contextSiteId]);

  const [busqueda, setBusqueda] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Helpers de fecha para FILTRADO (Query a Supabase)
  // Al filtrar, sí necesitamos UTC real o ajustar el string para cubrir el día completo
  const toIsoStartOfDay = (dateStr) => dateStr ? `${dateStr}T00:00:00.000Z` : null;
  const toIsoEndOfDay = (dateStr) => dateStr ? `${dateStr}T23:59:59.999Z` : null;

  const escapeHtml = (str) => String(str ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  
  // --- VISUALIZACIÓN DE FECHA ---
  // IMPORTANTE: timeZone: 'UTC' es clave para respetar la "Hora Forjada" del POS.
  // Si el POS guardó "15:00 Z" (que era 15:00 local), aquí mostramos 15:00 tal cual.
  const formatDateAR = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('es-AR', { timeZone: 'UTC' });
  };

  const formatTimeAR = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString('es-AR', { timeZone: 'UTC', hour: '2-digit', minute:'2-digit' });
  };

  const fmtDateTime = (iso) => iso ? `${formatDateAR(iso)} ${formatTimeAR(iso)}` : '-';

  const toDateOnly = (value) => {
    if (!value) return '';
    const raw = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const [datePart] = raw.split('T');
    return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : raw;
  };

  const getSaleDate = (venta) => venta?.fecha_venta || toDateOnly(venta?.created_at);
  const getBillingDate = (venta) => venta?.fecha_facturacion || toDateOnly(venta?.invoice_sent_at) || getSaleDate(venta);
  const getFiscalDate = (venta) => ((venta?.invoice_status === 'facturado' || venta?.cae) ? getBillingDate(venta) : getSaleDate(venta));
  const getFiscalTimeSource = (venta) => ((venta?.invoice_status === 'facturado' || venta?.cae) ? (venta?.invoice_sent_at || venta?.created_at) : venta?.created_at);

  useEffect(() => {
    if (profile?.organization_id) {
      cargarMaestros();
      fetchVentas();
    }
  }, [profile, filtroFechaInicio, filtroFechaFin, filtroSitio, filtroEmisor]);

  async function cargarMaestros() {
    try {
      const [
        { data: sitiosData },
        { data: org },
        { data: be },
        { data: methodsData },
      ] = await Promise.all([
        supabase
          .from('sitios')
          .select('id, nombre')
          .eq('organization_id', profile.organization_id)
          .order('nombre'),
        supabase
          .from('organizations')
          .select('id, name, cuit, tax_condition, print_config, full_address, phone')
          .eq('id', profile.organization_id)
          .maybeSingle(),
        supabase
          .from('billing_entities')
          .select('id, tax_name, cuit, tax_condition, is_active')
          .eq('organization_id', profile.organization_id)
          .order('tax_name', { ascending: true }),
        supabase
          .from('payment_methods')
          .select('id, name')
          .eq('organization_id', profile.organization_id)
          .eq('is_active', true)
          .order('name', { ascending: true }),
      ]);

      setSitios(sitiosData || []);
      setMetodosCobro(methodsData || []);
      if (filtroSitio && !(sitiosData || []).some((s) => s.id === filtroSitio)) {
        setFiltroSitio('');
      }

      if (org) {
        setOrgData(org);
        if (org.print_config) setPrintConfig(org.print_config);
      }

      if (be) setBillingEntities(be.filter(x => x?.is_active !== false));
    } catch (e) {
      console.error('[VentasHistorial] cargarMaestros falló:', e?.message || e, { code: e?.code, details: e?.details });
    }
  }

  async function fetchVentas() {
    try {
      setLoading(true);
      const start = toIsoStartOfDay(filtroFechaInicio);
      const end = toIsoEndOfDay(filtroFechaFin);

      const buildBaseQuery = () => {
        let q = supabase
          .from('sales')
          .select(`
            *,
            clients(full_name, doc_type, doc_number, tax_condition, address, city),
            sitios(nombre),
            sale_items(*, products!vinc_prod(name, iva_percentage, barcode)),
            store_orders!store_orders_sale_id_fkey(${SALE_STORE_ORDER_SELECT})
          `)
          .eq('organization_id', profile.organization_id)
          .eq('active', true)
          .neq('invoice_status', 'presupuesto')
          .order('created_at', { ascending: false });

        if (start && end) {
          q = q.gte('created_at', start).lte('created_at', end);
        }
        if (filtroSitio) q = q.or(buildHistorySiteFilter(filtroSitio));
        if (filtroEmisor) q = q.eq('billing_entity_id', filtroEmisor);
        return q;
      };

      // Supabase tope por defecto = 1000 filas. Paginamos para traer todo el rango.
      const PAGE_SIZE = 1000;
      const allRows = [];
      for (let page = 0; ; page += 1) {
        const from = page * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;
        const { data: pageData, error } = await buildBaseQuery().range(from, to);
        if (error) throw error;
        if (!pageData || pageData.length === 0) break;
        allRows.push(...pageData);
        if (pageData.length < PAGE_SIZE) break;
      }

      const attachedSalesRes = await attachStoreOrdersToSales({
        sales: allRows,
        organizationId: profile.organization_id,
        supabaseClient: supabase,
      });
      if (attachedSalesRes.error) throw attachedSalesRes.error;

      const visibleSales = (attachedSalesRes.data || []).filter((sale) => {
        return !isSaleAwaitingWebFulfillment(sale);
      });

      setVentas(visibleSales);
    } catch (error) {
      console.error('[VentasHistorial] fetchVentas falló:', error?.message || error, { code: error?.code, details: error?.details });
      reportHandledError(error, { feature: 'ventas:historial' });
      toast.error("Error al sincronizar historial");
    } finally {
      setLoading(false);
    }
  }

  // =====================================================================
  //  MOTOR DE IMPRESIÓN DINÁMICO (RESPETA CONFIG REPORTES)
  // =====================================================================
  
  // Emisor que facturó la venta. Se busca por id (y no en billingEntities, que
  // solo trae los activos): una factura vieja de un emisor dado de baja tiene
  // que reimprimirse con SUS datos y su CUIT en el QR.
  const fetchSaleBillingEntity = async (venta) => {
    if (!venta?.billing_entity_id) return null;
    const { data } = await supabase
      .from('billing_entities')
      .select('id, tax_name, cuit, tax_condition, fiscal_configuration, afip_pto_venta, iibb, inicio_actividades, domicilio_comercial')
      .eq('id', venta.billing_entity_id)
      .maybeSingle();
    return data || null;
  };

  const generarQrBase64Afip = async (venta, billingEntity) => {
    try {
      const url = buildAfipQrUrl({
        sale: venta,
        emitterCuit: billingEntity?.cuit || orgData?.cuit,
        client: venta.clients || null,
        ptoVentaFallback: billingEntity?.afip_pto_venta,
      });
      if (!url) return null;
      return await QRCode.toDataURL(url, { margin: 1, width: 300, errorCorrectionLevel: 'M' });
    } catch (e) {
      console.warn("Error generando QR AFIP:", e);
      return null;
    }
  };

  // Motor único de impresión: src/utils/ticketFiscal.js.
  const buildTicketHtml = async (venta) => {
    const isFiscal = venta.invoice_status === 'facturado' || !!venta.cae;
    const configKey = isFiscal ? 'factura' : 'ticket_venta';
    const config = printConfig?.[configKey] || {
        headerText: orgData?.name || 'Comprobante',
        footerText: 'Gracias por su compra',
        showLogo: true,
        showClientInfo: true,
        showPaymentMethod: true,
    };
    const billingEntity = await fetchSaleBillingEntity(venta);
    const qrBase64 = venta.cae ? await generarQrBase64Afip(venta, billingEntity) : null;
    return buildFiscalTicketHtml({
        sale: venta,
        items: venta.sale_items || [],
        client: venta.clients || null,
        emitter: resolveTicketEmitter({ organization: orgData, billingEntity }),
        config,
        qrDataUrl: qrBase64,
        siteName: venta.sitios?.nombre || '',
    });
  };

  const handleReimprimir = async (venta) => {
    const toastId = toast.loading("Generando ticket...");
    try {
      const html = await buildTicketHtml(venta);
      setPrintModal({ open: true, html });
      toast.dismiss(toastId);
    } catch (e) {
      toast.error("Error al generar comprobante.", { id: toastId });
    }
  };

  const handleAnularVenta = async (venta) => {
    if (venta.invoice_status === 'facturado') {
      toast.error("No se puede anular una factura emitida.");
      return;
    }
    setProcesandoAnulacion(true);
    const toastId = toast.loading("Anulando...");
    try {
      if (isCrmSale(venta)) {
        await retractCrmSaleMovement({
          saleId: venta.id,
          organizationId: profile.organization_id,
          actorUserId: profile?.id || null,
        });
        setVentas(prev => prev.filter(v => v.id !== venta.id));
        setConfirmingAnulacionId(null);
        toast.success("Venta anulada correctamente", { id: toastId });
        return;
      }

      // Reversión completa: estado + cuenta corriente + receivable + stock +
      // libro diario + banco (antes solo se revertía la cuenta corriente y el
      // stock/banco/libro diario quedaban con los valores de la venta anulada).
      const result = await annulSaleWithReversal({
        saleId: venta.id,
        organizationId: profile.organization_id,
        movementType: resolveSaleMovementType(venta),
      });

      if (result?.skipped && result.reason === 'already_annulled') {
        toast.success("La venta ya estaba anulada", { id: toastId });
      } else if (result?.reason === 'revision_sin_reversion') {
        // Venta cuyo cobro con terminal nunca se confirmó: no había stock ni
        // asientos que revertir.
        toast.success("Venta en revisión anulada (no había stock ni caja que revertir)", { id: toastId });
      } else {
        const restored = Number(result?.stock?.restoredCount || 0);
        toast.success(
          restored > 0
            ? "Venta anulada: stock, caja y cuenta corriente revertidos"
            : "Venta anulada correctamente",
          { id: toastId },
        );
      }

      // La anulación local ya se aplicó, pero si la devolución en Mercado Pago
      // falló la plata sigue cobrada: sin este aviso, nadie se entera.
      if (result?.refund?.attempted && result.refund.refunded === false) {
        toast.error(
          `La venta se anuló, pero NO se pudo devolver el cobro en Mercado Pago: ${result.refund.error || 'error desconocido'}. Hacelo desde el panel de MP.`,
          { duration: 14000 },
        );
      }
      setVentas(prev => prev.filter(v => v.id !== venta.id));
      setConfirmingAnulacionId(null);
    } catch (e) {
      toast.error("Error al anular", { id: toastId });
    } finally {
      setProcesandoAnulacion(false);
    }
  };

  const billingEntityMap = useMemo(() => {
    const map = new Map();
    (billingEntities || []).forEach(be => map.set(be.id, be));
    return map;
  }, [billingEntities]);

  const resolveEmisorLabel = (venta) => {
    const beId = venta?.billing_entity_id;
    if (beId && billingEntityMap.has(beId)) {
      const be = billingEntityMap.get(beId);
      return `${(be?.tax_name || 'Emisor').toUpperCase()} • CUIT ${be?.cuit || '-'}`;
    }
    return `${(orgData?.name || 'EMPRESA').toUpperCase()} • CUIT ${orgData?.cuit || '-'}`;
  };

  const metodoCobroOptions = useMemo(() => {
    const optionMap = new Map();

    (metodosCobro || []).forEach((method) => {
      const normalized = normalizePaymentMethod(method?.name);
      if (!normalized || optionMap.has(normalized)) return;
      optionMap.set(normalized, method.name);
    });

    (ventas || []).forEach((sale) => {
      const normalized = normalizePaymentMethod(sale?.payment_method);
      if (!normalized || optionMap.has(normalized)) return;
      optionMap.set(normalized, sale.payment_method);
    });

    return Array.from(optionMap.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es-AR', { sensitivity: 'base' }));
  }, [metodosCobro, ventas]);

  const filtrados = useMemo(() => {
    const q = (busqueda || '').toLowerCase().trim();
    const metodoSeleccionado = normalizePaymentMethod(filtroMetodoCobro);
    return ventas.filter(v => {
      const cliente = (v.clients?.full_name || '').toLowerCase();
      const matchBusqueda =
        cliente.includes(q)
        || String(v.id).toLowerCase().includes(q)
        || String(v.numero_factura).toLowerCase().includes(q);
      const matchMetodoCobro = !metodoSeleccionado
        ? true
        : normalizePaymentMethod(v.payment_method) === metodoSeleccionado;
      return matchBusqueda && matchMetodoCobro;
    });
  }, [ventas, busqueda, filtroMetodoCobro]);

  const handleSort = (key) => {
    setSortConfig((prev) => (
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'created_at' ? 'desc' : 'asc' }
    ));
  };

  const sortedVentas = useMemo(() => {
    const data = [...filtrados];
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;

    data.sort((a, b) => {
      let aVal = '';
      let bVal = '';

      switch (key) {
        case 'created_at':
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
          break;
        case 'cliente':
          aVal = String(a.clients?.full_name || 'Consumidor Final').toLowerCase();
          bVal = String(b.clients?.full_name || 'Consumidor Final').toLowerCase();
          break;
        case 'sucursal':
          aVal = String(a.sitios?.nombre || 'General').toLowerCase();
          bVal = String(b.sitios?.nombre || 'General').toLowerCase();
          break;
        case 'emisor':
          aVal = resolveEmisorLabel(a).toLowerCase();
          bVal = resolveEmisorLabel(b).toLowerCase();
          break;
        case 'pago':
          aVal = String(a.payment_method || 'N/A').toLowerCase();
          bVal = String(b.payment_method || 'N/A').toLowerCase();
          break;
        case 'total':
          aVal = Number(a.total || 0);
          bVal = Number(b.total || 0);
          break;
        default:
          aVal = String(a[key] || '').toLowerCase();
          bVal = String(b[key] || '').toLowerCase();
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        if (aVal < bVal) return -1 * dir;
        if (aVal > bVal) return 1 * dir;
        return 0;
      }

      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });

    return data;
  }, [filtrados, sortConfig, billingEntityMap, orgData]);

  // Paginación cliente (50 por defecto) — evita renderizar 1000+ filas
  const totalPages = Math.max(1, Math.ceil(sortedVentas.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStartIndex = (safeCurrentPage - 1) * pageSize;
  const pageEndIndex = Math.min(pageStartIndex + pageSize, sortedVentas.length);
  const ventasPagina = useMemo(
    () => sortedVentas.slice(pageStartIndex, pageEndIndex),
    [sortedVentas, pageStartIndex, pageEndIndex]
  );

  // Reset a página 1 cuando cambian filtros/orden/pageSize
  useEffect(() => {
    setCurrentPage(1);
  }, [busqueda, filtroMetodoCobro, filtroSitio, filtroEmisor, filtroFechaInicio, filtroFechaFin, sortConfig, pageSize]);

  // Clampear si la página actual queda fuera del rango por filtros
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const confirmingVenta = useMemo(
    () => ventas.find((venta) => venta.id === confirmingAnulacionId) || null,
    [ventas, confirmingAnulacionId],
  );

  const kpis = useMemo(() => {
    const total = filtrados.reduce((acc, v) => acc + (Number(v.total) || 0), 0);
    const count = filtrados.length;
    return { total, count, avg: count ? total / count : 0 };
  }, [filtrados]);

  const fmtMoney = (n) => Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 });
  const getSaleTerms = (venta) => buildSalePaymentTerms({ sale: venta, client: venta?.clients });

  const SortableTh = ({ label, sortKey, align = 'left', tooltip }) => {
    const isActive = sortConfig.key === sortKey;
    return (
      <th
        className={`px-3.5 py-3.5 cursor-pointer hover:bg-slate-100 transition-colors select-none ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'}`}
        onClick={() => handleSort(sortKey)}
        title={tooltip}
      >
        <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start'}`}>
          <div className="leading-tight text-inherit">{label}</div>
          <div className="flex flex-col">
            {isActive && sortConfig.direction === 'asc' && <ChevronUp size={10} className="text-[#51A7FD]" />}
            {isActive && sortConfig.direction === 'desc' && <ChevronDown size={10} className="text-[#51A7FD]" />}
            {!isActive && <FilterHint />}
          </div>
        </div>
      </th>
    );
  };

  const FilterHint = () => <div className="h-2.5 w-2.5 opacity-30" />;

  if (loading && ventas.length === 0) return (
    <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
      <RefreshCcw className="animate-spin mb-4 text-[#51A7FD]" size={40} />
      <p className="font-black uppercase tracking-[0.3em] text-slate-400 text-[10px]">Sincronizando Historial.</p>
    </div>
  );

  return (
    <div className="w-full px-3 py-3 sm:px-4 sm:py-4 lg:px-5 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      
      {/* HEADER - Estilo Productos */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <HistoryLog size={18} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-black text-slate-800 tracking-tight uppercase">Historial de Ventas</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Auditoría POS • Emisor (CUIT)</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap w-full lg:w-auto">
          <button
            onClick={() => { setExpandedId(null); fetchVentas(); }}
            className="flex-1 sm:flex-none justify-center px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-700 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
            title="Vuelve a consultar las ventas con los filtros actuales"
          >
            <RefreshCcw size={16} /> Sincronizar
          </button>
          <button
            onClick={() => navigate('/sistema/facturacion')}
            className="flex-1 sm:flex-none justify-center px-4 py-2.5 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
            title="Abre el modulo de facturacion para revisar comprobantes y estados"
          >
            <FileText size={16} /> Facturación
          </button>
        </div>
      </div>

      {/* Cobros con terminal que quedaron sin confirmar. Independiente de los
          filtros de fecha: una venta en revisión tiene que verse siempre. */}
      <CobrosAVerificar
        organizationId={profile?.organization_id}
        onResolved={fetchVentas}
      />

      {/* KPI - Estilo Productos */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5 mb-3">
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Volumen Total</p>
            <TrendingUp size={16} className="text-slate-300" />
          </div>
          <p className="mt-1 text-xl sm:text-2xl font-black text-slate-800 tracking-tight">$ {fmtMoney(kpis.total)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Ventas del periodo</p>
        </div>

        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Tickets</p>
            <ShoppingBag size={16} className="text-slate-300" />
          </div>
          <p className="mt-1 text-xl sm:text-2xl font-black text-emerald-600 tracking-tight">{kpis.count}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Operaciones realizadas</p>
        </div>

        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm sm:col-span-2 xl:col-span-1">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Promedio</p>
            <Info size={16} className="text-slate-300" />
          </div>
          <p className="mt-1 text-xl sm:text-2xl font-black text-amber-600 tracking-tight">$ {fmtMoney(kpis.avg)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Ticket promedio</p>
        </div>
      </div>

      {/* FILTROS - Estilo Productos */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4 mb-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-12 gap-2">
          {/* BUSCADOR */}
          <div className="sm:col-span-2 xl:col-span-2">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1" title="Busca por cliente, ID interno o numero de factura">
              <Search size={12} /> Buscar
            </label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
              <input
                type="text"
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                className="w-full pl-11 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner uppercase"
                placeholder="CLIENTE, ID, FACTURA..."
              />
            </div>
          </div>

          {/* FECHA INICIO */}
          <div className="xl:col-span-2">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1" title="Fecha inicial del rango a consultar">
              <Calendar size={12} /> Fecha desde
            </label>
            <input
              type="date"
              value={filtroFechaInicio}
              onChange={e => setFiltroFechaInicio(e.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner"
            />
          </div>

          {/* FECHA FIN */}
          <div className="xl:col-span-2">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1" title="Fecha final del rango a consultar">
              <Calendar size={12} /> Fecha hasta
            </label>
            <input
              type="date"
              value={filtroFechaFin}
              onChange={e => setFiltroFechaFin(e.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner"
            />
          </div>

          {/* SUCURSAL */}
          <div className="xl:col-span-2">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1" title="Por defecto toma la sucursal seleccionada en la topbar">
              <MapPin size={12} /> Sucursal
            </label>
            <select
              value={filtroSitio}
              onChange={e => setFiltroSitio(e.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
            >
              <option value="">TODAS</option>
              {sitios.map(s => <option key={s.id} value={s.id}>{s.nombre.toUpperCase()}</option>)}
            </select>
          </div>

          {/* EMISOR */}
          <div className="xl:col-span-2">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1" title="Filtra por emisor fiscal utilizado en la venta">
              <Building2 size={12} /> Emisor
            </label>
            <select
              value={filtroEmisor}
              onChange={e => setFiltroEmisor(e.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
            >
              <option value="">TODOS</option>
              {billingEntities.map(be => <option key={be.id} value={be.id}>{be.tax_name.toUpperCase()}</option>)}
            </select>
          </div>

          <div className="xl:col-span-2">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1" title="Filtra por metodo de cobro registrado en la venta">
              <CreditCard size={12} /> Metodo de cobro
            </label>
            <select
              value={filtroMetodoCobro}
              onChange={e => setFiltroMetodoCobro(e.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
            >
              <option value="">TODOS</option>
              {metodoCobroOptions.map((metodo) => (
                <option key={metodo.value} value={metodo.value}>
                  {String(metodo.label || '').toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* TABLA - Estilo Productos */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-[#0B2A36] px-4 sm:px-5 py-3 border-b border-white/10 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <HistoryLog size={16} className="text-[#3FF2AA]" />
            <span className="text-[10px] font-black text-white uppercase tracking-widest">Listado de Operaciones ({filtrados.length})</span>
          </div>
        </div>

        <div className="lg:hidden p-3 space-y-2.5 bg-slate-50/40">
          {ventasPagina.map((venta) => {
            const isExpanded = expandedId === venta.id;
            const saleTerms = getSaleTerms(venta);

            return (
              <div
                key={`mobile-${venta.id}`}
                className={`rounded-2xl border shadow-sm overflow-hidden transition-all ${isExpanded ? 'border-[#51A7FD]/30 bg-white' : 'border-slate-200 bg-white'}`}
              >
                <button
                  onClick={() => setExpandedId(isExpanded ? null : venta.id)}
                  className="w-full p-3 text-left"
                  title="Ver detalle completo de la venta"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{fmtDateTime(venta.created_at)}</p>
                      <p className="mt-1 text-[13px] font-black text-slate-800 uppercase truncate">
                        {venta.clients?.full_name || 'Consumidor Final'}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <span className="text-[9px] font-black px-2 py-1 bg-slate-100 text-slate-600 rounded-xl border border-slate-200 uppercase">
                          {venta.sitios?.nombre || 'General'}
                        </span>
                        <span className="text-[9px] font-black px-2 py-1 bg-slate-100 text-slate-600 rounded-xl border border-slate-200 uppercase">
                          {venta.payment_method || 'N/A'}
                        </span>
                        <span className="text-[9px] font-black px-2 py-1 bg-blue-50 text-blue-700 rounded-xl border border-blue-100 uppercase">
                          {saleTerms.label || 'N/A'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Total</p>
                      <p className="mt-1 text-[16px] font-black text-slate-900 tracking-tight">$ {fmtMoney(venta.total)}</p>
                      <div className="mt-2 flex justify-end">
                        {isExpanded ? <ChevronUp size={16} className="text-[#51A7FD]" /> : <ChevronDown size={16} className="text-slate-300" />}
                      </div>
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-3 pb-3">
                    <div className="border-t border-slate-100 pt-3 space-y-2.5">
                      <div className="flex flex-wrap gap-2">
                        {venta.invoice_status !== 'facturado' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmingAnulacionId(venta.id); }}
                            className="flex-1 min-w-[140px] px-3 py-2 rounded-xl border border-rose-100 text-rose-500 hover:bg-rose-50 transition-all text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
                            title="Anula la venta si no fue facturada y revierte sus efectos"
                          >
                            <Trash2 size={12} /> Anular Venta
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleReimprimir(venta); }}
                          className="flex-1 min-w-[140px] px-3 py-2 rounded-xl bg-[#3FF2AA] text-[#0B2A36] hover:brightness-95 transition-all text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-sm"
                          title="Genera nuevamente el comprobante para imprimir"
                        >
                          <Printer size={12} /> Reimprimir
                        </button>
                      </div>

                      <div className="grid grid-cols-1 gap-2">
                        <div className="bg-slate-50 border border-slate-100 rounded-2xl overflow-hidden">
                          <div className="px-3 py-2.5 border-b border-slate-100 flex items-center justify-between gap-2">
                            <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Articulos</p>
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{countLogicalSaleItems(venta.sale_items)} item(s)</span>
                          </div>
                          <div className="divide-y divide-slate-100">
                            {buildSaleItemsDisplayList(venta.sale_items).map((item, idx) => (
                              <div
                                key={idx}
                                className={`px-3 py-2.5 flex items-start justify-between gap-3 ${item.isComboComponent ? 'bg-slate-50/40 pl-6' : 'bg-white/80'}`}
                              >
                                <div className="min-w-0">
                                  <p className={`${item.isComboComponent ? 'text-[10px] text-slate-500' : 'text-[11px] text-slate-700'} font-black uppercase truncate`}>
                                    {item.isComboComponent ? 'L ' : ''}{getSaleItemDisplayName(item)}
                                  </p>
                                  <p className="text-[9px] font-bold text-slate-400 uppercase mt-0.5">x{item.quantity} | $ {fmtMoney(item.unit_price)} c/u</p>
                                </div>
                                <span className={`shrink-0 font-black ${item.isComboComponent ? 'text-[10px] text-slate-500' : 'text-[11px] text-slate-800'}`}>$ {fmtMoney(item.unit_price * item.quantity)}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3">
                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Estado</p>
                            <p className="mt-1 text-[11px] font-black text-slate-700 uppercase">{venta.invoice_status || 'Pendiente'}</p>
                          </div>
                          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3">
                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Emisor</p>
                            <p className="mt-1 text-[11px] font-black text-slate-700 uppercase truncate">{resolveEmisorLabel(venta)}</p>
                          </div>
                          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3">
                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Factura</p>
                            <p className="mt-1 text-[11px] font-black text-slate-700">{venta.numero_factura || 'N/A'}</p>
                          </div>
                          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3">
                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">CAE</p>
                            <p className="mt-1 text-[11px] font-black text-slate-700 truncate">{venta.cae || 'N/A'}</p>
                          </div>
                          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3">
                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Condicion</p>
                            <p className="mt-1 text-[11px] font-black text-slate-700 uppercase">{saleTerms.label || 'N/A'}</p>
                          </div>
                          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3">
                            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Vencimiento</p>
                            <p className="mt-1 text-[11px] font-black text-slate-700">{saleTerms.dueDate ? formatDateAR(saleTerms.dueDate) : '-'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {filtrados.length === 0 && !loading && (
            <div className="px-4 py-14 text-center flex flex-col items-center opacity-50">
              <FileText size={40} className="text-slate-300 mb-3 stroke-1" />
              <p className="text-xs font-bold text-slate-400 uppercase">No se encontraron ventas</p>
            </div>
          )}
        </div>

        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full min-w-[860px] xl:min-w-[900px]">
            <thead>
              <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                <SortableTh label="Fecha y Hora" sortKey="created_at" tooltip="Ordena por fecha y hora de la operacion" />
                <SortableTh label="Cliente" sortKey="cliente" tooltip="Ordena alfabeticamente por cliente" />
                <SortableTh label="Sucursal" sortKey="sucursal" tooltip="Ordena por sucursal de origen" />
                <SortableTh label="Emisor / CUIT" sortKey="emisor" tooltip="Ordena por emisor fiscal configurado" />
                <SortableTh label="Pago" sortKey="pago" align="center" tooltip="Ordena por metodo de pago usado" />
                <SortableTh label="Total" sortKey="total" align="right" tooltip="Ordena por importe total de la venta" />
                <th className="px-4 py-3.5 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {ventasPagina.map((venta) => {
                const isExpanded = expandedId === venta.id;
                const saleTerms = getSaleTerms(venta);
                
                return (
                  <React.Fragment key={venta.id}>
                    <tr
                      onClick={() => setExpandedId(isExpanded ? null : venta.id)}
                      className={`hover:bg-blue-50/20 transition-all cursor-pointer group ${isExpanded ? 'bg-blue-50/40' : ''}`}
                    >
                      <td className="px-4 py-3.5 text-[11px] font-bold text-slate-500 whitespace-nowrap">
                        {fmtDateTime(venta.created_at)}
                      </td>
                      <td className="px-3.5 py-3.5">
                        <div className="text-[11px] font-black text-slate-800 uppercase truncate" title={venta.clients?.full_name || 'Consumidor Final'}>
                          {venta.clients?.full_name || 'Consumidor Final'}
                        </div>
                      </td>
                      <td className="px-3.5 py-3.5" title={venta.sitios?.nombre || 'General'}>
                        <div className="text-[9px] text-slate-400 font-bold uppercase flex items-center gap-1">
                          <MapPin size={10} className="text-[#51A7FD]" /> {venta.sitios?.nombre || 'General'}
                        </div>
                      </td>
                      <td className="px-3.5 py-3.5">
                        <span className="text-[9px] font-black px-2.5 py-1 bg-slate-100 text-slate-600 rounded-xl border border-slate-200 uppercase" title={resolveEmisorLabel(venta)}>
                          {resolveEmisorLabel(venta)}
                        </span>
                      </td>
                      <td className="px-3.5 py-3.5 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-[9px] font-black text-slate-600 uppercase bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-xl" title={venta.payment_method || 'N/A'}>
                            {venta.payment_method || 'N/A'}
                          </span>
                          <span className="text-[9px] font-black text-blue-700 uppercase bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-xl" title={saleTerms.label || 'N/A'}>
                            {saleTerms.label || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-3.5 py-3.5 text-right" title={`Total cobrado: $ ${fmtMoney(venta.total)}`}>
                        <span className="text-slate-900 font-black text-[13px] tracking-tighter">
                          $ {fmtMoney(venta.total)}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        {isExpanded ? <ChevronUp size={16} className="text-[#51A7FD]" title="Ocultar detalle" /> : <ChevronDown size={16} className="text-slate-300 group-hover:text-[#51A7FD]" title="Ver detalle" />}
                      </td>
                    </tr>
                    
                    {isExpanded && (
                      <tr className="bg-slate-50/30 animate-in slide-in-from-top-1 duration-200">
                        <td colSpan="7" className="px-4 py-3">
                          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-4 shadow-sm">
                            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-2.5 border-b border-slate-100 pb-2.5 mb-3">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                                  <ShoppingBag size={14} className="text-[#51A7FD]" />
                                  <span>Detalle de articulos</span>
                                </div>
                                <p className="mt-1.5 text-[10px] sm:text-[11px] text-slate-500 leading-relaxed">
                                  Vista compacta de items, total cobrado y datos del comprobante.
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {venta.invoice_status !== 'facturado' && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setConfirmingAnulacionId(venta.id); }}
                                    className="px-4 py-2 rounded-xl border border-rose-100 text-rose-500 hover:bg-rose-50 transition-all text-[9px] font-black uppercase tracking-widest flex items-center gap-2"
                                    title="Anula la venta si no fue facturada y revierte sus efectos"
                                  >
                                    <Trash2 size={12} /> Anular Venta
                                  </button>
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleReimprimir(venta); }}
                                  className="px-3.5 py-2 rounded-xl bg-[#3FF2AA] text-[#0B2A36] hover:brightness-95 transition-all text-[9px] font-black uppercase tracking-widest flex items-center gap-2 shadow-sm"
                                  title="Genera nuevamente el comprobante para imprimir"
                                >
                                  <Printer size={12} /> Reimprimir
                                </button>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.65fr)_minmax(300px,1fr)] gap-2.5 sm:gap-3">
                              <div className="bg-slate-50/80 border border-slate-100 rounded-2xl overflow-hidden">
                                <div className="px-3 py-2.5 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-xl bg-white border border-slate-100">
                                      <ShoppingBag size={14} className="text-[#51A7FD]" />
                                    </span>
                                    <div>
                                      <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Articulos</p>
                                      <p className="text-[10px] text-slate-400">Detalle de la venta seleccionada</p>
                                    </div>
                                  </div>
                                  <span className="px-2.5 py-1 rounded-xl bg-white border border-slate-100 text-[9px] font-black text-slate-500 uppercase tracking-widest">
                                    {countLogicalSaleItems(venta.sale_items)} item(s)
                                  </span>
                                </div>

                                <div className="divide-y divide-slate-100">
                                  {buildSaleItemsDisplayList(venta.sale_items).map((item, idx) => (
                                    <div
                                      key={idx}
                                      className={`px-3 py-2.5 flex items-center justify-between gap-3 ${item.isComboComponent ? 'bg-slate-50/50 pl-8' : 'bg-white/70'}`}
                                    >
                                      <div className="min-w-0 flex items-start gap-2.5">
                                        <span className={`mt-0.5 inline-flex min-w-[38px] justify-center rounded-xl px-2 py-1 font-black uppercase tracking-widest ${item.isComboComponent ? 'bg-white border border-slate-100 text-[7px] text-slate-400' : 'bg-slate-100 text-[8px] text-slate-600'}`}>
                                          x{item.quantity}
                                        </span>
                                        <div className="min-w-0">
                                          <span
                                            className={`block font-black truncate uppercase ${item.isComboComponent ? 'text-[10px] text-slate-500' : 'text-[11px] text-slate-700'}`}
                                            title={getSaleItemDisplayName(item)}
                                          >
                                            {item.isComboComponent ? 'L ' : ''}{getSaleItemDisplayName(item)}
                                          </span>
                                          <span className="block text-[9px] font-bold text-slate-400 uppercase">
                                            $ {fmtMoney(item.unit_price)} c/u
                                          </span>
                                        </div>
                                      </div>
                                      <span className={`shrink-0 font-black ${item.isComboComponent ? 'text-[10px] text-slate-500' : 'text-[12px] text-slate-800'}`}>
                                        $ {fmtMoney(item.unit_price * item.quantity)}
                                      </span>
                                    </div>
                                  ))}
                                  {(!venta.sale_items || venta.sale_items.length === 0) && (
                                    <div className="px-4 py-6 text-center">
                                      <div className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-white border border-slate-100 mb-2.5">
                                        <Info size={16} className="text-slate-300" />
                                      </div>
                                      <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">
                                        Sin articulos cargados
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="space-y-2.5">
                                <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3">
                                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3">Resumen</p>
                                  <div className="flex items-end justify-between gap-2.5">
                                    <div>
                                      <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Total</p>
                                      <p className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight">$ {fmtMoney(venta.total)}</p>
                                    </div>
                                    <span className={`text-[9px] font-black uppercase px-2.5 py-1 rounded-xl border ${
                                      venta.invoice_status === 'facturado'
                                        ? 'text-emerald-600 bg-emerald-50 border-emerald-100'
                                        : 'text-amber-600 bg-amber-50 border-amber-100'
                                    }`}>
                                      {venta.invoice_status || 'Pendiente'}
                                    </span>
                                  </div>
                                  <div className="mt-3 grid grid-cols-2 gap-2">
                                    <div className="bg-white border border-slate-100 rounded-xl px-3 py-2">
                                      <label className="text-[8px] font-black text-slate-400 uppercase block">Fecha venta</label>
                                      <span className="text-[10px] font-bold text-slate-700">{formatDateAR(getSaleDate(venta))}</span>
                                    </div>
                                    <div className="bg-white border border-slate-100 rounded-xl px-3 py-2">
                                      <label className="text-[8px] font-black text-slate-400 uppercase block">Hora</label>
                                      <span className="text-[10px] font-bold text-slate-700">{formatTimeAR(venta.created_at)}</span>
                                    </div>
                                  </div>
                                </div>

                                <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100">
                                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-4">Datos de la operacion</p>
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className="bg-white border border-slate-100 rounded-xl px-3 py-2">
                                      <label className="text-[8px] font-black text-slate-400 uppercase block">ID Interno</label>
                                      <span className="text-[10px] font-bold text-slate-700">#{venta.id.slice(-8).toUpperCase()}</span>
                                    </div>
                                    <div className="bg-white border border-slate-100 rounded-xl px-3 py-2">
                                      <label className="text-[8px] font-black text-slate-400 uppercase block">Metodo</label>
                                      <span className="text-[10px] font-bold text-slate-700 uppercase">{venta.payment_method || 'N/A'}</span>
                                    </div>
                                    <div className="bg-white border border-slate-100 rounded-xl px-3 py-2">
                                      <label className="text-[8px] font-black text-slate-400 uppercase block">Punto Venta / Nro</label>
                                      <span className="text-[10px] font-bold text-slate-700">{venta.numero_factura || 'N/A'}</span>
                                    </div>
                                    <div className="bg-white border border-slate-100 rounded-xl px-3 py-2">
                                      <label className="text-[8px] font-black text-slate-400 uppercase block">CAE</label>
                                      <span className="text-[10px] font-bold text-slate-700">{venta.cae || 'N/A'}</span>
                                    </div>
                                    <div className="bg-white border border-slate-100 rounded-xl px-3 py-2">
                                      <label className="text-[8px] font-black text-slate-400 uppercase block">Condicion pago</label>
                                      <span className="text-[10px] font-bold text-slate-700 uppercase">{saleTerms.label || 'N/A'}</span>
                                    </div>
                                    <div className="bg-white border border-slate-100 rounded-xl px-3 py-2">
                                      <label className="text-[8px] font-black text-slate-400 uppercase block">Vencimiento</label>
                                      <span className="text-[10px] font-bold text-slate-700">{saleTerms.dueDate ? formatDateAR(saleTerms.dueDate) : '-'}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filtrados.length === 0 && !loading && (
                  <tr>
                      <td colSpan="7" className="px-6 py-20 text-center flex flex-col items-center opacity-50">
                          <FileText size={48} className="text-slate-300 mb-4 stroke-1"/>
                          <p className="text-xs font-bold text-slate-400 uppercase">No se encontraron ventas</p>
                      </td>
                  </tr>
              )}
            </tbody>
          </table>
        </div>

        {sortedVentas.length > 0 && (
          <div className="border-t border-slate-100 bg-white px-3 py-3 sm:px-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 text-center sm:text-left">
              Mostrando {pageStartIndex + 1}-{pageEndIndex} de {sortedVentas.length}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value) || DEFAULT_PAGE_SIZE)}
                className="w-full sm:w-auto rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-500 outline-none"
                title="Cantidad de ventas visibles por pagina"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size} por pagina</option>
                ))}
              </select>
              <div className="flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={safeCurrentPage <= 1}
                  className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-[10px] font-black uppercase tracking-widest text-slate-500 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <span className="min-w-[74px] text-center text-[10px] font-black uppercase tracking-widest text-slate-500">
                  {safeCurrentPage}/{totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={safeCurrentPage >= totalPages}
                  className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-[10px] font-black uppercase tracking-widest text-slate-500 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal Reimpresión */}
      {printModal.open && (
        <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
          <div className="bg-white w-full max-w-4xl h-[88vh] sm:h-[85vh] rounded-3xl overflow-hidden flex flex-col shadow-2xl">
            <div className="bg-[#0B2A36] p-3 sm:p-5 flex flex-col sm:flex-row justify-between sm:items-center gap-3 text-white">
              <div className="flex items-center gap-3">
                <FileText size={20} className="text-[#3FF2AA]"/>
                <span className="font-black text-[10px] uppercase tracking-[0.2em]">Vista previa de impresión</span>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <button onClick={() => { iframeRef.current?.contentWindow?.print(); }} className="flex-1 sm:flex-none bg-[#3FF2AA] text-[#0B2A36] px-4 sm:px-6 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg">Imprimir ahora</button>
                <button onClick={()=>setPrintModal({open:false, html:''})} className="bg-white/10 p-2.5 rounded-xl hover:bg-white/20 transition-all"><X size={20}/></button>
              </div>
            </div>
            <iframe ref={iframeRef} srcDoc={printModal.html} className="flex-1 w-full bg-slate-100 border-none"/>
          </div>
        </div>
      )}

      {/* Confirmación de Anulación */}
      {confirmingAnulacionId && (
          <div className="fixed inset-0 z-[10000] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
              <div className="bg-white p-5 sm:p-8 rounded-3xl max-w-sm w-full shadow-2xl text-center">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-rose-50 text-rose-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <AlertTriangle size={32} />
                  </div>
                  <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight mb-2">¿Anular Venta?</h3>
                  <p className="text-[11px] font-bold text-slate-400 uppercase leading-relaxed mb-6">Esta acción devolverá el stock a la sucursal y restará el dinero de la caja. No se puede deshacer.</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                      <button onClick={() => setConfirmingAnulacionId(null)} className="flex-1 px-4 py-3 bg-slate-100 text-slate-600 rounded-2xl font-black text-[10px] uppercase tracking-widest">Cancelar</button>
                      <button 
                        onClick={() => handleAnularVenta(confirmingVenta)} 
                        className="flex-1 px-4 py-3 bg-rose-500 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-rose-200"
                      >
                        {procesandoAnulacion ? <RefreshCcw className="animate-spin mx-auto" size={16}/> : 'Confirmar'}
                      </button>
                  </div>
              </div>
          </div>
      )}

    </div>
  );
}

export default VentasHistorial;


